import { PaginatedAPIResponse } from "../models/NetworkResponse";
import { PurchaseRequestRejectedBy } from "../models/PurchaseRequest/PurchaseRequestBody";
import {
  PurhcaseRequestDetails,
  PurhcaseRequestFilters,
} from "../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestProduct } from "../models/PurchaseRequest/PurchaseRequestProductInsights";
import { VendorsPOReportAPIFilters } from "../models/Vendors/VendorsPOReportFilters";
import { downloadPDF, getAPI, postAPI, putAPI } from "./Networking";

export default class PurchaseRequestAPI {
  getPurchaseRequestListing = async (
    filters: PurhcaseRequestFilters,
  ): Promise<PaginatedAPIResponse<PurhcaseRequestDetails>> => {
    try {
      const queryParams = new URLSearchParams();

      if (
        filters.pr_created_by_admin_ids &&
        Array.isArray(filters.pr_created_by_admin_ids)
      ) {
        const adminIds = filters.pr_created_by_admin_ids
          .map((item) => item.id)
          .join(",");
        queryParams.append("pr_created_by_admin_ids", adminIds);
      }

      if (filters.order_id && Array.isArray(filters.order_id)) {
        const orderIds = filters.order_id.map((item) => item.id).join(",");
        queryParams.append("order_id", orderIds);
      }

      if (filters.page_no !== undefined)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit !== undefined)
        queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text);

      if (filters.sort_by) queryParams.append("sort_by", filters.sort_by);

      if (filters.sort_type) queryParams.append("sort_type", filters.sort_type);

      if (filters.expected_payment_date_from)
        queryParams.append(
          "expected_payment_date_from",
          filters.expected_payment_date_from.format("YYYY-MM-DD"),
        );

      if (filters.expected_payment_date_to)
        queryParams.append(
          "expected_payment_date_to",
          filters.expected_payment_date_to.format("YYYY-MM-DD"),
        );

      if (filters.created_date_from)
        queryParams.append(
          "created_date_from",
          filters.created_date_from.format("YYYY-MM-DD"),
        );

      if (filters.created_date_to)
        queryParams.append(
          "created_date_to",
          filters.created_date_to.format("YYYY-MM-DD"),
        );

      if (filters.pending_approval_type?.length) {
        filters.pending_approval_type.forEach((type) => {
          queryParams.append(type, "true");
        });
      }

      if (filters.approved_type?.length) {
        filters.approved_type.forEach((type) => {
          queryParams.append(type, "true");
        });
      }

      if (filters.pr_status?.length) {
        queryParams.append("pr_status", String(filters.pr_status[0].id));
      }

      const response = await getAPI(
        `/v5/purchase-requests?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approvePurchaseRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
  ) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/approve-request/${prId}`,
        {
          team: team,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  rejectPurchaseRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
    rejectionReason?: string,
  ) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/reject-request/${prId}`,
        {
          team: team,
          rejection_reason: rejectionReason,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  generatePO = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/generate-po/${prId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestDetailByPOId = async (
    poId: number,
  ): Promise<PurhcaseRequestDetails> => {
    try {
      const response = await getAPI(
        `/v5/purchase-requests/details/po-id/${poId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestDetail = async (
    prId: number,
  ): Promise<PurhcaseRequestDetails> => {
    try {
      const response = await getAPI(`/v5/purchase-requests/${prId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPrProductAvailableQty = async (productIds: number[], orderId: number) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/product-available-qty`,
        { product_ids: productIds, order_id: orderId },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadPurchaseOrder = async (po_id: number) => {
    try {
      const response = await downloadPDF(
        `/v5/purchase-requests/download-po/${po_id}`,
      );
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestProduct = async (product: PurchaseRequestProduct) => {
    const response = await postAPI(`/v5/purchase-requests/update-pr-product`, {
      product: product,
    });
    return response;
  };

  sendEmailToVendor = async (pr_id: number, po_id: number) => {
    const response = await postAPI(
      `/admin/purchaseOrder/${pr_id}/vendor/send-email`,
      {
        po_id: po_id,
      },
    );
    return response;
  };

  updatePOStatus = async (
    po_id: number,
    status: string,
    cancellationReason?: string,
  ) => {
    const response = await postAPI(
      `/admin/purchaseOrder/update-status/` + po_id,
      {
        status: status,
        poCancellingReason: cancellationReason || "",
      },
    );
    return response;
  };

  cancelPurchaseRequest = async (
    pr_id: number,
    cancellationReason?: string,
  ) => {
    const response = await postAPI(
      `/v5/purchase-requests/cancel-request/${pr_id}`,
      {
        cancellation_reason: cancellationReason,
      },
    );
    return response;
  };

  getVendorsPOReportData = async (filters: VendorsPOReportAPIFilters) => {
    const response = await getAPI(
      `/v5/purchase-requests/vendors/po-report-data`,
      filters,
    );
    return response;
  };

  reactivatePO = async (prId: number, poId: number) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/reactivate-po/${prId}`,
        { po_id: poId },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
